import classNames from 'classnames'
import { ReleaseCard } from 'components/release-calendar/ReleaseCard'
import { ReleaseCardSkeleton } from 'components/release-calendar/ReleaseCardSkeleton'
import { useAuth } from 'contexts'
import { MappedReleases } from 'contexts/providers/ReleaseCalendarProvider'
import dayjs from 'dayjs'
import { useUserProfile } from 'hooks'
import { Translate } from 'next-translate'
import useTranslation from 'next-translate/useTranslation'
import { PropsWithChildren } from 'react'
import Skeleton from 'react-loading-skeleton'
import { ContainerElement } from 'types'
import { range } from 'utils/arrays'
import { getUserTimezone } from 'utils/data'

type ShouldDisplayDateProps = {
  shouldDisplayDate: boolean
}

type Props = {
  mappedReleases: MappedReleases
  skeletonsCount?: number
} & ShouldDisplayDateProps

function getTotalIndex(id: number, mappedReleases: MappedReleases) {
  return Object.values(mappedReleases)
    .flat()
    .findIndex(el => el.id === id)
}

/**
 * Parses a given string and formats it in a displayable manner.
 */
function getFormattedReleaseDate(date: string, timezone: string, t: Translate) {
  const current = dayjs().tz(timezone)
  const releaseDate = dayjs(date)

  const isSameMonthAndYear =
    current.get('month') === releaseDate.get('month') &&
    current.get('year') === releaseDate.get('year')

  if (isSameMonthAndYear && current.get('D') === releaseDate.get('D')) {
    return t('today')
  }

  if (isSameMonthAndYear && current.get('D') === releaseDate.get('D') - 1) {
    return t('tomorrow')
  }

  if (isSameMonthAndYear && current.get('D') === releaseDate.get('D') + 1) {
    return t('yesterday')
  }

  return releaseDate.isValid() ? releaseDate.format('ddd Do MMM') : t(date)
}

export const Releases = ({
  mappedReleases,
  skeletonsCount = 12,
  shouldDisplayDate,
}: Props) => {
  const entries = Object.entries(mappedReleases)
  const hasData = Boolean(entries.length)

  return (
    <div className='mx-auto w-full max-w-[90rem] overflow-hidden'>
      <ReleasesList>
        {!hasData && (
          <ReleasesSkeletonsItems
            count={skeletonsCount}
            shouldDisplayDate={shouldDisplayDate}
          />
        )}

        {hasData && <ReleasesItems mappedReleases={mappedReleases} />}
      </ReleasesList>

      <SmallReleasesList>
        {!hasData && <SmallReleasesSkeletonsItems count={skeletonsCount} />}

        {hasData && <SmallReleasesItems mappedReleases={mappedReleases} />}
      </SmallReleasesList>
    </div>
  )
}

/**
 * Displays the `ol` element corresponding to the list of releases for screens larger than 369px.
 */
export function ReleasesList({ children, className }: ContainerElement) {
  return (
    <ol
      className={classNames(
        'hidden w-[calc(100%+1rem)] grid-cols-2 gap-y-10 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 [@media(min-width:369px)]:grid',
        className
      )}
    >
      {children}
    </ol>
  )
}

type ReleaseItemProps = {
  totalIndex: number
} & PropsWithChildren &
  ShouldDisplayDateProps

/**
 * Display the `li` element corresponding to a release list element on screens larger than 369px.
 *
 * Also determines if it should be displayed or not, depending on whether the user is authenticated and the screen width.
 */
function ReleaseItem({
  totalIndex,
  shouldDisplayDate,
  children,
}: ReleaseItemProps) {
  const { user } = useAuth()

  return (
    <li
      className={classNames({
        'relative pt-3': shouldDisplayDate,
        hidden: !user && totalIndex >= 4,
        'sm:block md:hidden': !user && totalIndex < 6,
        'md:!block': !user && totalIndex < 4,
        'xl:!block': !user && totalIndex < 5,
      })}
    >
      {children}
    </li>
  )
}

/**
 * Given an object of mapped (grouped) releases, displays the releases for larger screens, without displaying the `ol` element.
 */
export function ReleasesItems({
  mappedReleases,
}: {
  mappedReleases: MappedReleases
}) {
  const { t } = useTranslation('release_calendar')
  const { data: userProfile } = useUserProfile()
  const timezone = getUserTimezone(userProfile)
  const entries = Object.entries(mappedReleases)

  return (
    <>
      {entries.map(([key, releases], pIdx) => {
        if (!releases.length) return null

        const shouldDisplayDate = dayjs(key).isValid()

        return releases.map((release, sIdx) => {
          const totalIndex = getTotalIndex(release.id, mappedReleases)
          const isLastItem =
            totalIndex === Object.values(mappedReleases).flat().length - 1
          const isLastItemOTD = sIdx === releases.length - 1

          return (
            <ReleaseItem
              key={`release-${pIdx}@${sIdx}`}
              shouldDisplayDate={shouldDisplayDate}
              totalIndex={totalIndex}
            >
              {sIdx === 0 && shouldDisplayDate && (
                <h4 className='absolute top-0 z-[1] -ml-4 bg-cool-50 px-2.5 pl-4 font-body text-base font-medium tracking-normal'>
                  {getFormattedReleaseDate(key, timezone, t)}
                </h4>
              )}

              <ReleaseCardContainer
                shouldDisplayDate={shouldDisplayDate}
                isLastItem={isLastItem}
              >
                <ReleaseCard {...release} className='w-full' />

                <DateArrow
                  shouldDisplayDate={shouldDisplayDate}
                  isLastItem={isLastItem}
                  isLastItemOTD={isLastItemOTD}
                />
              </ReleaseCardContainer>
            </ReleaseItem>
          )
        })
      })}
    </>
  )
}

type ReleaseCardContainerProps = {
  isLastItem: boolean
} & PropsWithChildren &
  ShouldDisplayDateProps

function ReleaseCardContainer({
  shouldDisplayDate,
  isLastItem,
  children,
}: ReleaseCardContainerProps) {
  return (
    <div
      className={classNames(
        shouldDisplayDate &&
          'relative border-t border-dashed border-cool-400 pt-4',
        isLastItem ? 'mr-4' : 'pr-4'
      )}
    >
      {children}
    </div>
  )
}

type DateArrowProps = {
  isLastItem: boolean
  /**
   * is last item of the day
   */
  isLastItemOTD: boolean
} & ShouldDisplayDateProps

function DateArrow({
  isLastItem,
  shouldDisplayDate,
  isLastItemOTD,
}: DateArrowProps) {
  if (!shouldDisplayDate || !isLastItemOTD) return null

  return (
    <div
      className={classNames(
        'absolute -top-[4.5px] h-0 w-0 border-y-[4px] border-l-[6px] border-y-transparent border-l-cool-400',
        isLastItem ? 'right-0' : 'right-[16px]'
      )}
    ></div>
  )
}

export function ReleasesSkeletonsItems({
  count = 12,
  shouldDisplayDate,
}: {
  count?: number
} & ShouldDisplayDateProps) {
  return (
    <>
      {range(0, count).map(i => {
        const isLastItem = i === 1 || i === 3 // just a placeholder, these values don't really mean anything

        return (
          <ReleaseItem
            key={i}
            totalIndex={i}
            shouldDisplayDate={shouldDisplayDate}
          >
            <ReleaseCardContainer
              isLastItem={isLastItem}
              shouldDisplayDate={shouldDisplayDate}
            >
              <ReleaseCardSkeleton />

              <DateArrow
                shouldDisplayDate={shouldDisplayDate}
                isLastItem={isLastItem}
                isLastItemOTD={isLastItem}
              />
            </ReleaseCardContainer>
          </ReleaseItem>
        )
      })}
    </>
  )
}

// =================== <= 369px ======================= //

/**
 * Displays the `ol` element corresponding to the list of releases for screens smaller than or equal to 369px.
 */
export function SmallReleasesList({ children, className }: ContainerElement) {
  return (
    <ol
      className={classNames(
        'mt-10 space-y-20 [@media(min-width:369px)]:hidden',
        className
      )}
    >
      {children}
    </ol>
  )
}

type SmallReleaseItemProps = {
  totalIndex: number
} & ContainerElement

/**
 * Displays the `li` element corresponding to a releast list element on screens smaller than or equal to 369px.
 *
 * Also determines if it should be displayed or not, depending on whether the user is authenticated.
 */
function SmallReleaseItem({
  totalIndex,
  children,
  className,
}: SmallReleaseItemProps) {
  const { user } = useAuth()

  return (
    <li
      className={classNames(className, {
        hidden: !user && totalIndex >= 4,
      })}
    >
      {children}
    </li>
  )
}

/**
 * Given an object of mapped (grouped) releases, displays the releases for smaller screens, without displaying the `ol` element.
 */
export function SmallReleasesItems({
  mappedReleases,
}: {
  mappedReleases: MappedReleases
}) {
  const { t } = useTranslation('release_calendar')
  const { data: userProfile } = useUserProfile()
  const timezone = getUserTimezone(userProfile)
  const entries = Object.entries(mappedReleases)

  return (
    <>
      {entries.map(([key, releases], pIdx) => {
        const shouldDisplayDate = dayjs(key).isValid()

        return (
          <li key={key} className='[&:has(li.hidden)]:hidden'>
            <ol className='relative grid grid-cols-1 gap-x-4 gap-y-8 [@media(min-width:369px)]:grid-cols-2'>
              {releases.map((release, sIdx) => {
                const totalIndex = getTotalIndex(release.id, mappedReleases)

                return (
                  <SmallReleaseItem
                    key={`${pIdx}@${sIdx}`}
                    totalIndex={totalIndex}
                  >
                    {sIdx === 0 && shouldDisplayDate && (
                      <h4 className='absolute -top-10 font-body text-lg font-medium tracking-normal'>
                        {getFormattedReleaseDate(key, timezone, t)}
                      </h4>
                    )}

                    <ReleaseCard {...release} />
                  </SmallReleaseItem>
                )
              })}
            </ol>
          </li>
        )
      })}
    </>
  )
}

export function SmallReleasesSkeletonsItems({
  count = 12,
}: {
  count?: number
}) {
  return (
    <>
      {range(0, count).map(i => (
        <SmallReleaseItem key={i} totalIndex={i} className='relative'>
          <Skeleton
            className='h-full w-2/3'
            containerClassName='absolute -top-10 h-6 w-full'
          />

          <ReleaseCardSkeleton />
        </SmallReleaseItem>
      ))}
    </>
  )
}
